import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/lib/context/auth-context";
import { SupportTicket, TicketFilter } from "@/lib/types/support-tickets";
import { getTickets, subscribeToNewTickets, subscribeToTicketUpdates } from "@/lib/services/support-ticket-service";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Plus, TicketIcon, LifeBuoy } from "lucide-react";
import TicketCard from "@/components/support/TicketCard";
import TicketFilters from "@/components/support/TicketFilters";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/components/ui/use-toast";
import { RealtimeChannel } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

const TicketsList = () => {
  const { profile } = useAuth();
  const isStaff = profile?.role === 'staff' || profile?.role === 'admin' || profile?.role === 'tech';
  
  // State for ticket filters
  const [filters, setFilters] = useState<TicketFilter>({
    status: undefined,
    type: undefined,
    priority: undefined,
    searchQuery: ""
  });

  // Query for tickets
  const {
    data: tickets,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['tickets', filters],
    queryFn: () => getTickets(filters),
    enabled: !!profile
  });

  // Set up realtime subscriptions
  useEffect(() => {
    if (!profile) return;

    let newTicketsChannel: RealtimeChannel | null = null;
    let ticketUpdatesChannel: RealtimeChannel | null = null;

    // Subscribe to new tickets
    newTicketsChannel = subscribeToNewTickets((newTicket) => {
      // Only notify if we're a staff member or if the ticket is ours
      if (isStaff || newTicket.created_by === profile.id) {
        toast({
          title: "New Ticket Created",
          description: `Ticket: ${newTicket.title}`,
        });
        refetch();
      }
    });

    // Subscribe to ticket updates
    ticketUpdatesChannel = subscribeToTicketUpdates((updatedTicket) => {
      // Only notify if we're a staff member or if the ticket is ours
      if (isStaff || updatedTicket.created_by === profile.id) {
        toast({
          title: "Ticket Updated",
          description: `Ticket: ${updatedTicket.title} - Status: ${updatedTicket.status}`,
        });
        refetch();
      }
    });

    // Cleanup function
    return () => {
      if (newTicketsChannel) supabase.removeChannel(newTicketsChannel);
      if (ticketUpdatesChannel) supabase.removeChannel(ticketUpdatesChannel);
    };
  }, [profile, isStaff, refetch]);

  // Filter change handler
  const handleFilterChange = (newFilters: TicketFilter) => {
    setFilters(newFilters);
  };

  // Render loading skeleton
  if (isLoading) {
    return (
      <div className="container mx-auto py-8 max-w-6xl">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-title text-primary">Support Tickets</h1>
          <Skeleton className="h-10 w-32" />
        </div>
        <Skeleton className="h-16 w-full mb-6" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="container mx-auto py-8 max-w-6xl text-center">
        <h1 className="text-3xl font-title text-primary mb-4">Oops! Something went wrong</h1>
        <p className="text-muted-foreground mb-6">
          We couldn't load the tickets. Please try again later.
        </p>
        <Button onClick={() => refetch()}>Try Again</Button>
      </div>
    );
  }

  return (
    <div className="w-full py-1 px-1">
      <div className="page-header">
        <div className="flex items-center gap-2">
          <LifeBuoy className="h-6 w-6 text-[#004161]" />
          <h1>Support Tickets</h1>
        </div>
        <p>
          {isStaff 
            ? "Manage and respond to user support tickets" 
            : "View and manage your support tickets"}
        </p>
      </div>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <Button asChild>
          <Link to="/support/tickets/new">
            <Plus className="mr-2 h-4 w-4" /> New Ticket
          </Link>
        </Button>
      </div>

      <TicketFilters filters={filters} onFilterChange={handleFilterChange} />

      {tickets && tickets.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {tickets.map((ticket) => (
            <TicketCard key={ticket.id} ticket={ticket} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <TicketIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-title mb-2">No tickets found</h2>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            {Object.keys(filters).some(key => 
              filters[key as keyof TicketFilter] !== undefined && 
              (Array.isArray(filters[key as keyof TicketFilter]) 
                ? (filters[key as keyof TicketFilter] as any[]).length > 0 
                : !!filters[key as keyof TicketFilter])
            )
              ? "No tickets match your current filters. Try adjusting your filters or create a new ticket."
              : isStaff 
                ? "There are no support tickets in the system yet."
                : "You haven't submitted any support tickets yet. Create a new ticket to get help."}
          </p>
          <Button asChild>
            <Link to="/support/tickets/new">
              <Plus className="mr-2 h-4 w-4" /> Create New Ticket
            </Link>
          </Button>
        </div>
      )}
    </div>
  );
};

export default TicketsList;
